// src/pages/actividades/components/mobile/ActividadCard.tsx
import { CheckCircle2, Circle, Clock, Star, Check, CalendarClock } from 'lucide-react';
import { cn, formatMinutes } from '@/lib/utils';
import { useSwipeAction } from '@/hooks/useSwipeAction';
import type { ActividadListItem } from '@/types';

interface ActividadCardProps {
  actividad: ActividadListItem;
  onTap: (id: string) => void;
  onToggleComplete: (id: string) => void;
  onPostpone?: (id: string) => void;
  isSubtarea?: boolean;
}

const PRIORITY_COLORS: Record<string, string> = {
  AAA: 'bg-red-500 text-white',
  AA: 'bg-orange-500 text-white',
  A: 'bg-blue-500 text-white',
  B: 'bg-gray-400 text-white',
  C: 'bg-gray-300 text-gray-700',
};

const STATUS_LABELS: Record<string, string> = {
  PENDIENTE: 'Pendiente',
  EN_PROCESO: 'En Proceso',
  EN_ESPERA: 'En Espera',
  EN_REVISION: 'En Revisión',
  COMPLETADA: 'Completada',
  CANCELADA: 'Cancelada',
};

const STATUS_COLORS: Record<string, string> = {
  PENDIENTE: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  EN_PROCESO: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  EN_ESPERA: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  EN_REVISION: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  COMPLETADA: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  CANCELADA: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200',
};

export function ActividadCard({
  actividad,
  onTap,
  onToggleComplete,
  onPostpone,
  isSubtarea = false,
}: ActividadCardProps) {
  const isCompleted = actividad.estado === 'COMPLETADA' || actividad.estado === 'CANCELADA';

  const swipeRef = useSwipeAction<HTMLDivElement>({
    onSwipeRight: () => onToggleComplete(actividad.id),
    onSwipeLeft: onPostpone ? () => onPostpone(actividad.id) : undefined,
  });

  return (
    <div className="relative overflow-hidden">
      {/* Swipe reveal backgrounds */}
      <div className="absolute inset-0 flex">
        <div className="flex-1 bg-green-500 flex items-center pl-4">
          <Check className="h-5 w-5 text-white" />
        </div>
        <div className="flex-1 bg-amber-500 flex items-center justify-end pr-4">
          <CalendarClock className="h-5 w-5 text-white" />
        </div>
      </div>

      {/* Card content */}
      <div
        ref={swipeRef}
        className={cn(
          'relative flex items-start gap-3 px-4 py-3 border-b border-border bg-background active:bg-muted/50 transition-colors',
          isSubtarea && 'pl-10',
          isCompleted && 'opacity-60'
        )}
      >
        {/* Checkbox */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleComplete(actividad.id);
          }}
          className="min-w-touch min-h-touch flex items-center justify-center -m-2 shrink-0"
          aria-label={isCompleted ? 'Reabrir' : 'Completar'}
        >
          {isCompleted ? (
            <CheckCircle2 className="h-5 w-5 text-green-500" />
          ) : (
            <Circle className="h-5 w-5 text-muted-foreground" />
          )}
        </button>

        {/* Content — tappable */}
        <div
          className="flex-1 min-w-0 cursor-pointer"
          onClick={() => onTap(actividad.id)}
        >
          {/* Row 1: Title + Priority */}
          <div className="flex items-start gap-2">
            <span
              className={cn(
                'text-sm font-medium line-clamp-2 flex-1',
                isCompleted && 'line-through text-muted-foreground'
              )}
            >
              {actividad.titulo}
            </span>
            <span
              className={cn(
                'shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded',
                PRIORITY_COLORS[actividad.prioridad] || 'bg-gray-300 text-gray-700'
              )}
            >
              {actividad.prioridad}
            </span>
          </div>

          {/* Row 2: Metadata */}
          <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
            {actividad.cliente_nombre && (
              <span className="truncate max-w-[120px]">{actividad.cliente_nombre}</span>
            )}
            {actividad.responsable_nombre && (
              <>
                {actividad.cliente_nombre && <span>·</span>}
                <span className="truncate max-w-[100px]">@{actividad.responsable_nombre}</span>
              </>
            )}
            {actividad.tiempo_registrado_min > 0 && (
              <>
                <span>·</span>
                <span className="flex items-center gap-0.5">
                  <Clock className="h-3 w-3" />
                  {formatMinutes(actividad.tiempo_registrado_min)}
                </span>
              </>
            )}
          </div>

          {/* Row 3: Status + Subtasks + Focus */}
          <div className="flex items-center gap-2 mt-1">
            {actividad.estado !== 'PENDIENTE' && (
              <span
                className={cn(
                  'text-[10px] font-medium px-1.5 py-0.5 rounded-full',
                  STATUS_COLORS[actividad.estado] || 'bg-gray-100 text-gray-800'
                )}
              >
                {STATUS_LABELS[actividad.estado] || actividad.estado}
              </span>
            )}
            {(actividad.subtareas_count ?? 0) > 0 && (
              <span className="text-[10px] text-muted-foreground">
                {actividad.subtareas_count} subtareas
              </span>
            )}
            {actividad.en_foco && (
              <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
